/**
 * Reimbursements API service. Endpoints mirror Angular default-list / default-edit.
 */
import apiClient from '@/lib/api/apiClient'
import { LIBERTY_ORG_ID } from '../constants'
import type {
  ReimbursementFilters,
  ReimbursementListItem,
  ReimbursementListResponse,
  PropertyOption,
  CompanyOption,
  OrganizationOption,
  PolicyDeclarationFilesResponse,
  ExportStatus,
  ReimbursementDetail,
  ApplicationDetail,
  RentalStatusList,
  CancelClaimPayload,
  DenyReopenClaimPayload,
  PaymentPayload,
  PropertyPaymentInfo,
  DefaultEmail,
  ReviewResidentPayload,
  ClaimReviewPayload,
  ClaimReviewLogPayload,
  StatementPayload,
  StatementEmailPayload,
  NoteItem,
  PaymentApprovalPayload,
} from '../types'


function serializeListParams(filters: Record<string, unknown>): string {
  const parts: string[] = []

  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === '') continue
    let processedValue = typeof value === 'string' ? value.trim() : value
    if (processedValue === '') continue
    if (Array.isArray(processedValue) && processedValue.length === 0) continue

    // Convert React's YYYY-MM-DD to Angular's expected DD-MM-YYYY
    if (
      [
        'notification_fromDt',
        'notification_toDt',
        'lease_start_fromDt',
        'lease_start_toDt',
        'claim_status_fromDt',
        'claim_status_toDt',
      ].includes(key) &&
      typeof processedValue === 'string'
    ) {
      if (/^\d{4}-\d{2}-\d{2}$/.test(processedValue)) {
        const [yyyy, mm, dd] = processedValue.split('-')
        processedValue = `${dd}-${mm}-${yyyy}`
      }
    }

    if (Array.isArray(processedValue)) {
      processedValue.forEach((val, idx) => {
        const strVal = typeof val === 'string' ? val.trim() : val
        if (strVal !== '') {
          parts.push(`${encodeURIComponent(key)}[${idx}]=${encodeURIComponent(String(strVal))}`)
        }
      })
    } else {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(processedValue))}`)
    }
  }

  return parts.join('&')
}

const LIST_ORDER_BY_TO_API: Record<string, string> = {
  reimbursement_rental_status: 'reimbursement_rental_status',
  reimbursement_rental_progress: 'reimbursement_rental_progress',
}

function mapListOrderByToApi(orderBy: string | undefined): string | undefined {
  if (!orderBy) return orderBy
  return LIST_ORDER_BY_TO_API[orderBy] ?? orderBy
}

function mapListItemFromApi(row: ReimbursementListItem): ReimbursementListItem {
  const legacy = row as ReimbursementListItem & {
    rental_status?: string
    rental_progress?: string
  }
  return {
    ...row,
    reimbursement_rental_status:
      row.reimbursement_rental_status ?? legacy.rental_status,
    reimbursement_rental_progress:
      row.reimbursement_rental_progress ?? legacy.rental_progress,
  }
}

/**
 * Switches between the liberty endpoint and the charity (atlanta) endpoint
 * based on the active org id, matching Angular default-list behavior.
 */
export async function getReimbursements(
  filters: ReimbursementFilters,
  orgId: number | string | undefined,
): Promise<ReimbursementListResponse> {
  const endpoint = Number(orgId) === LIBERTY_ORG_ID ? '/api/default' : '/api/atlanta_defaults'
  const apiFilters: ReimbursementFilters = {
    ...filters,
    orderBy: mapListOrderByToApi(filters.orderBy),
  }
  const response = await apiClient.get<ReimbursementListResponse>(endpoint, {
    params: apiFilters,
    paramsSerializer: { serialize: (p) => serializeListParams(p) },
  })
  const data = response.data
  return {
    ...data,
    data: (data.data ?? []).map(mapListItemFromApi),
  }
}

export async function searchOrganizations(q: string): Promise<OrganizationOption[]> {
  const response = await apiClient.get<OrganizationOption[]>('/api/all_organizations', {
    params: { q },
  })
  return response.data ?? []
}

export async function searchCompanies(
  q: string,
  orgId?: number | string,
): Promise<CompanyOption[]> {
  const params: Record<string, unknown> = { q }
  if (orgId !== undefined && orgId !== '') params.org_id = orgId
  const response = await apiClient.get<CompanyOption[]>('/api/all_company', { params })
  return response.data ?? []
}

export async function searchProperties(
  q: string,
  orgId?: number | string,
  companyId?: number | string,
): Promise<PropertyOption[]> {
  const params: Record<string, unknown> = { q }
  if (orgId !== undefined && orgId !== '') params.org_id = orgId
  if (companyId !== undefined && companyId !== '') params.company_id = companyId
  const response = await apiClient.get<PropertyOption[]>('/api/all_property', { params })
  return response.data ?? []
}

export async function getCertificateUrl(applicationId: string | number): Promise<string> {
  const response = await apiClient.get<{ certificatePath?: string } | string>(
    `/api/certificate/${applicationId}`,
  )
  if (typeof response.data === 'string') return response.data
  return response.data?.certificatePath ?? ''
}

export async function getPolicyDeclarationFiles(
  applicationId: string | number,
): Promise<PolicyDeclarationFilesResponse> {
  const response = await apiClient.get<PolicyDeclarationFilesResponse>(
    `/policy-declaration-files/${applicationId}`,
  )
  return response.data ?? { files: [] }
}

export async function downloadServeFileBlob(path: string): Promise<Blob> {
  const response = await apiClient.get<Blob>(path, { responseType: 'blob' })
  return response.data
}

/**
 * Triggers a server-side export job. Caller should immediately poll
 * `getLatestExportStatus` to surface the resulting download link.
 * Response body mirrors Angular `exportFile` subscribe `result` (includes `message` for snackbar).
 */
export async function exportReimbursements(
  filters: ReimbursementFilters,
  scope: 'export' | 'exportAll',
): Promise<{ message?: string }> {
  const endpoint = scope === 'exportAll' ? '/api/export/default_all' : '/api/export/default'
  const exportParams = {
    ...filters,
    org_id:
      filters.org_id !== undefined && filters.org_id !== null && String(filters.org_id).trim() !== ''
        ? filters.org_id
        : '1',
    view: 'xlsx',
  }
  const response = await apiClient.get<{ message?: string } | string>(endpoint, {
    params: exportParams,
    paramsSerializer: { serialize: (p) => serializeListParams(p) },
  })
  const data = response.data
  if (typeof data === 'string') return { message: data }
  if (data && typeof data === 'object') return data
  return {}
}

export async function getLatestExportStatus(userId: string | number): Promise<ExportStatus> {
  const response = await apiClient.get<ExportStatus>(
    `/api/export/get_letest_report_for_user/${userId}`,
    { params: { report_type: 'reports_all_default' } },
  )
  return response.data
}

/**
 * Inline-edit and detail submit. Accepts either a FormData (when files are present)
 * or a plain object for JSON updates.
 */
export async function updateReimbursement(
  userId: string | number,
  payload: FormData | Record<string, unknown>,
): Promise<{ message?: string }> {
  const response = await apiClient.post<{ message?: string }>(
    `/api/default/${userId}`,
    payload,
  )
  return response.data ?? {}
}

export type ReimbursementSectionApiKey = 'core' | 'unique' | 'final'

export async function submitReimbursementSection(
  userId: string | number,
  applicationId: string | number,
  section: ReimbursementSectionApiKey,
  extraFields?: Record<string, unknown>,
): Promise<{ message?: string }> {
  return updateReimbursement(userId, {
    page: 'default',
    application_id: applicationId,
    reimbursement_section_submit: section,
    ...extraFields,
  })
}

export async function unlockReimbursementSection(
  userId: string | number,
  applicationId: string | number,
  section: ReimbursementSectionApiKey,
  extraFields?: Record<string, unknown>,
): Promise<{ message?: string }> {
  return updateReimbursement(userId, {
    page: 'default',
    application_id: applicationId,
    reimbursement_section_unlock: section,
    ...extraFields,
  })
}

/** Liberty — all sections APPROVED; sets reimbursement rental_status / rental_progress on the server. */
export async function submitReimbursementForLibertyReview(
  userId: string | number,
  applicationId: string | number,
  extraFields?: Record<string, unknown>,
): Promise<{ message?: string }> {
  return updateReimbursement(userId, {
    page: 'default',
    application_id: applicationId,
    reimbursement_submit_for_review: '1',
    ...extraFields,
  })
}

export async function deleteReimbursementFile(
  fileId: string | number,
): Promise<{ message?: string }> {
  const response = await apiClient.delete<{ message?: string }>(`/api/default/${fileId}`)
  return response.data ?? {}
}

export async function getReimbursementDetail(
  userId: string | number,
  applicationId: string | number,
  reimbursementId?: string | number,
): Promise<ReimbursementDetail> {
  // Angular PR #1170 `loadResidentDetail` (default-detail): page:'default' + reimbursement_detail.
  const params: Record<string, unknown> = {
    page: 'default',
    reimbursement_detail: true,
    application_id: applicationId,
  }
  if (
    reimbursementId !== undefined &&
    reimbursementId !== null &&
    String(reimbursementId).trim() !== ''
  ) {
    params.reimbursement_id = String(reimbursementId)
  }
  const response = await apiClient.get<ReimbursementDetail>(`/api/default/${userId}`, {
    params,
  })
  return response.data
}

export async function getApplication(applicantId: string | number): Promise<ApplicationDetail> {
  // Angular `getApplicationInfoRequestParams` (resident-edit-new.component.ts:453-454).
  const response = await apiClient.get<ApplicationDetail>(
    `/api/application_info/${applicantId}`,
    { params: { reimbursement_detail: true } },
  )
  return response.data
}

let rentalStatusListCache: Promise<RentalStatusList> | null = null

export async function getRentalStatusList(): Promise<RentalStatusList> {
  if (!rentalStatusListCache) {
    rentalStatusListCache = apiClient
      .get<RentalStatusList | string>('/default_list')
      .then((r) => {
        if (typeof r.data === 'string' && /<!doctype html>/i.test(r.data)) {
          throw new Error('Invalid /default_list response (received HTML instead of JSON)')
        }
        return r.data as RentalStatusList
      })
      .catch((e) => {
        rentalStatusListCache = null
        throw e
      })
  }
  return rentalStatusListCache
}

export async function cancelClaim(
  defaultId: string | number,
  payload: CancelClaimPayload,
): Promise<{ message?: string }> {
  const response = await apiClient.post<{ message?: string }>(
    `/api/default_cancel_claim/${defaultId}`,
    payload,
  )
  return response.data ?? {}
}

export async function denyReopenClaim(
  defaultId: string | number,
  payload: DenyReopenClaimPayload,
): Promise<{ message?: string }> {
  const response = await apiClient.post<{ message?: string }>(
    `/api/default_denyreopen_claim/${defaultId}`,
    payload,
  )
  return response.data ?? {}
}

export async function createDefaultPayment(
  defaultId: string | number,
  payload: PaymentPayload,
): Promise<{ message?: string }> {
  const response = await apiClient.post<{ message?: string }>(
    `/api/default_payment/${defaultId}`,
    payload,
  )
  return response.data ?? {}
}

export async function updateDefaultPayment(
  paymentLogId: string | number,
  payload: PaymentPayload,
): Promise<{ message?: string }> {
  const response = await apiClient.post<{ message?: string }>(
    `/api/update_default_payment/${paymentLogId}`,
    payload,
  )
  return response.data ?? {}
}

export async function getPropertyPaymentInfo(
  propertyId: string | number,
): Promise<PropertyPaymentInfo> {
  const response = await apiClient.get<PropertyPaymentInfo>(`/api/property/${propertyId}`)
  return response.data
}

export async function getActiveDefaultEmails(
  propertyId: string | number,
): Promise<DefaultEmail[]> {
  const response = await apiClient.get<DefaultEmail[]>(
    `/api/company_active_default_email/${propertyId}`,
  )
  return response.data ?? []
}

export async function getEorpDocument(
  id: string | number,
): Promise<{ is_active: boolean; eorp_section: string }> {
  try {
    const response = await apiClient.get<{ is_active: boolean; eorp_section: string }>(
      `/api/eorp/${id}`,
    )
    return response.data
  } catch (error) {
    // Preserve API body for showApiError (Angular `notify(error.error, 0)`).
    if (error && typeof error === 'object') throw error
    throw error
  }
}

export { getNewEorp, downloadNewEorpPdf } from '@/lib/api/eorpService'

export async function reviewResidentDocuments(
  defaultId: string | number,
  payload: ReviewResidentPayload,
): Promise<{ message?: string }> {
  const response = await apiClient.post<{ message?: string }>(
    `/api/resident_review_status/${defaultId}`,
    payload,
  )
  return response.data ?? {}
}

export async function reviewClaimStatus(
  defaultId: string | number,
  payload: ClaimReviewPayload,
): Promise<{ message?: string }> {
  const response = await apiClient.post<{ message?: string }>(
    `/api/resident_claim_review_status/${defaultId}`,
    payload,
  )
  return response.data ?? {}
}

export async function updateClaimReviewLog(
  reviewLogId: string | number,
  payload: ClaimReviewLogPayload,
): Promise<{ message?: string }> {
  const response = await apiClient.post<{ message?: string }>(
    `/api/claim_review_log/${reviewLogId}`,
    payload,
  )
  return response.data ?? {}
}

export async function approveDefaultPayment(
  paymentLogId: string | number,
  payload: PaymentApprovalPayload,
): Promise<{ message?: string }> {
  const response = await apiClient.post<{ message?: string }>(
    `/api/default_payment_approved/${paymentLogId}`,
    payload,
  )
  return response.data ?? {}
}

export async function updatePaymentLog(
  paymentLogId: string | number,
  payload: Record<string, unknown>,
): Promise<{ message?: string }> {
  const response = await apiClient.post<{ message?: string }>(
    `/api/payment_log/${paymentLogId}`,
    payload,
  )
  return response.data ?? {}
}

export async function markDefaultForfeit(
  defaultId: string | number,
  payload: Record<string, unknown> = {},
): Promise<{ message?: string }> {
  const response = await apiClient.post<{ message?: string }>(
    `/api/default_forfeit/${defaultId}`,
    payload,
  )
  return response.data ?? {}
}

export async function updateClaimLock(
  defaultId: string | number,
): Promise<{ message?: string }> {
  const response = await apiClient.post<{ message?: string }>(
    `/api/update_claim_lock/${defaultId}`,
  )
  return response.data ?? {}
}

export async function updateExcludeAuto(
  defaultId: string | number,
  payload: Record<string, unknown>,
): Promise<{ message?: string }> {
  const response = await apiClient.post<{ message?: string }>(
    `/api/default_exclude_auto/${defaultId}`,
    payload,
  )
  return response.data ?? {}
}

export async function generateDefaultPaymentStatement(
  defaultId: string | number,
  payload: StatementPayload,
): Promise<{ message?: string }> {
  const response = await apiClient.post<{ message?: string }>(
    `/api/default_payment_statement/${defaultId}`,
    payload,
  )
  return response.data ?? {}
}

export async function sendDefaultPaymentStatementEmail(
  defaultId: string | number,
  payload: StatementEmailPayload,
): Promise<{ message?: string }> {
  const response = await apiClient.post<{ message?: string }>(
    `/api/default_payment_statement_email/${defaultId}`,
    payload,
  )
  return response.data ?? {}
}

export async function uploadOldStatement(
  defaultId: string | number,
  payload: FormData,
): Promise<{ message?: string }> {
  const response = await apiClient.post<{ message?: string }>(
    `/api/default/old_statement/${defaultId}`,
    payload,
  )
  return response.data ?? {}
}

export async function uploadDefaultAttachment(
  defaultId: string | number,
  payload: FormData,
): Promise<{ message?: string }> {
  const response = await apiClient.post<{ message?: string }>(
    `/api/attachments/default/${defaultId}`,
    payload,
  )
  return response.data ?? {}
}

export async function getDefaultAttachments(
  defaultId: string | number,
): Promise<unknown[]> {
  const response = await apiClient.get<unknown[]>(`/api/attachments/default/${defaultId}`)
  return response.data ?? []
}

export async function deleteDefaultAttachment(
  fileId: string | number,
): Promise<{ message?: string }> {
  const response = await apiClient.delete<{ message?: string }>(
    `/api/attachments/default/${fileId}`,
  )
  return response.data ?? {}
}

export async function getDefaultNotes(
  defaultId: string | number,
  page = 1,
  params?: Record<string, unknown>,
): Promise<NoteItem[]> {
  const response = await apiClient.get<NoteItem[]>(`/api/notes/${defaultId}`, {
    params: { page, ...(params ?? {}) },
  })
  return response.data ?? []
}

export async function addDefaultNote(
  defaultId: string | number,
  payload: Record<string, unknown>,
): Promise<{ message?: string }> {
  const response = await apiClient.post<{ message?: string }>(`/api/notes/${defaultId}`, payload)
  return response.data ?? {}
}

export async function verifyEmailAddress(userId: string | number): Promise<string> {
  // Angular assigns the GET body to `email_verify`. Axios may parse an empty/odd body as `{}`,
  // which is truthy — React cannot render that object (crash in Settlement Emails Status).
  const response = await apiClient.get<unknown>(`/api/email_verify/${userId}`)
  return normalizeEmailVerifyStatus(response.data)
}

/** Coerce `/api/email_verify/:id` body to a display/status string (Angular template-safe). */
export function normalizeEmailVerifyStatus(raw: unknown): string {
  if (typeof raw === 'string') return raw.trim()
  if (raw == null) return ''
  if (typeof raw === 'number' || typeof raw === 'boolean') return String(raw)
  if (typeof raw === 'object') {
    const obj = raw as Record<string, unknown>
    if (Object.keys(obj).length === 0) return ''
    const nested =
      obj.email_verify ?? obj.status ?? obj.data ?? obj.message ?? obj.email_verify_status
    if (typeof nested === 'string') return nested.trim()
    if (typeof nested === 'number' || typeof nested === 'boolean') return String(nested)
    return ''
  }
  return ''
}
